import React, { useState, useEffect, forwardRef } from 'react';
import { 
  validatePhoneNumber, 
  formatPhoneForDisplay, 
  getPhonePlaceholder,
  PhoneValidationResult,
  DEFAULT_PHONE_CONFIG 
} from '../utils/phoneValidation';
import { useTranslation } from '../hooks/useTranslation';

interface PhoneInputProps {
  value: string;
  onChange: (value: string, validation: PhoneValidationResult) => void;
  onBlur?: (validation: PhoneValidationResult) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  showValidation?: boolean;
  error?: string;
  label?: string;
  id?: string;
  name?: string;
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(({
  value,
  onChange,
  onBlur,
  placeholder,
  className = '',
  disabled = false,
  required = false,
  showValidation = true,
  error: externalError,
  label,
  id,
  name
}, ref) => {
  const { t } = useTranslation();
  const [displayValue, setDisplayValue] = useState('');
  const [validation, setValidation] = useState<PhoneValidationResult>({
    isValid: false,
    formattedNumber: '',
    isInternational: false
  });
  const [isFocused, setIsFocused] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  // Update display value when value prop changes
  useEffect(() => {
    if (value) {
      setDisplayValue(formatPhoneForDisplay(value));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  // Validate phone number on value change (only update local state)
  useEffect(() => {
    if (value) {
      const validationResult = validatePhoneNumber(value);
      setValidation(validationResult);
    } else {
      const emptyValidation: PhoneValidationResult = {
        isValid: false,
        formattedNumber: '',
        isInternational: false
      };
      setValidation(emptyValidation);
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow typing but clean the input for validation
    setDisplayValue(inputValue);
    
    // Remove formatting for validation
    const cleanValue = inputValue.replace(/\s+/g, '').replace(/[^\d\+]/g, '');
    
    // Call onChange with clean value
    const validationResult = validatePhoneNumber(cleanValue);
    setValidation(validationResult);
    onChange(cleanValue, validationResult);
  };

  const handleBlur = () => {
    setIsFocused(false);
    setHasBlurred(true);
    
    // Format the display value on blur
    if (value) {
      setDisplayValue(formatPhoneForDisplay(value));
    }
    
    if (onBlur) {
      onBlur(validation);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  // Determine if we should show an error
  const shouldShowError = showValidation && hasBlurred && !isFocused && 
    (validation.errorMessage || externalError);

  const errorMessage = externalError || validation.errorMessage;

  // Determine input styling
  const getInputClassName = () => {
    let baseClass = 'input-field mt-1 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
    
    if (disabled) {
      baseClass += ' bg-gray-100 cursor-not-allowed';
    }
    
    if (shouldShowError) {
      baseClass += ' border-red-300 focus:ring-red-500 focus:border-red-500';
    } else if (validation.isValid && hasBlurred) {
      baseClass += ' border-green-300 focus:ring-green-500 focus:border-green-500';
    } else {
      baseClass += ' border-gray-300';
    }
    
    return `${baseClass} ${className}`;
  };

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <input
        ref={ref}
        id={id}
        name={name}
        type="tel"
        autoComplete="tel"
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder || getPhonePlaceholder()}
        disabled={disabled}
        required={required}
        className={getInputClassName()}
      />
      
      {/* Validation feedback */}
      {showValidation && (
        <div className="mt-1">
          {shouldShowError && (
            <>
              <p className="text-sm text-red-600">
                {errorMessage}
              </p>
              {validation.errorMessage === 'No se permite registrar teléfonos fijos porque no pueden recibir SMS. Por favor, ingresa un número de celular.' && (
                <div className="bg-red-100 border border-red-300 text-red-800 text-xs rounded p-2 mt-1">
                  {errorMessage}
                </div>
              )}
            </>
          )}
          
          {validation.isValid && hasBlurred && !shouldShowError && (
            <p className="text-sm text-green-600">
              ✓ {t('validPhoneNumber')}
            </p>
          )}
          
          {!hasBlurred && !validation.isValid && value && (
            <p className="text-sm text-gray-500">
              {validation.isInternational ? t('internationalNumber') : t('localNumber')}
            </p>
          )}
        </div>
      )}
      
      {/* Help text */}
      {!hasBlurred && !value && (
        <p className="mt-1 text-xs text-gray-500">
          {t('enterPhoneNumber')}
        </p>
      )}
    </div>
  );
});

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput; 